/**
 * REQUEST_PUBLISH_EXECUTION —— 「商家在聊天里要求发布」这条指令的认领与执行。
 *
 * 这个工具**自己不发布**(yepairag `publishing.py`,`readOnlyHint: True`)。它只是个信号:
 * 前端认出它、走确定性直发栈把内容发出去、再把逐渠道结果作为普通消息回传,Maya 据此收尾。
 * 工具的 docstring 明确要求她调完就 **WAIT** —— 所以前端不执行,整条链就永远停在那里。
 *
 * 而在 2026-08-28 之前,认这个信号的代码是 `ContentStudioPage` 里的一个 `useEffect`,
 * 依赖 `chat.tools`(**当前活跃会话**正在流的那个 turn 的工具列表)。三个条件缺一即静默丢失:
 * 页面挂载着、正好在看这个会话、turn 还没结束(`message-complete` 会把 tools 折进 traces
 * 并清空 `state.tools`)。dev 实测窗口只有 3 秒:
 *
 *     18:10:22  商家 "Publish it to my Shopify blog now."
 *     18:10:27  ← 切到 Marketing Hub(不刷新)
 *     18:10:27  tool_call REQUEST_PUBLISH_EXECUTION
 *     18:10:30  done
 *     结果:published_content 29 → 29,一条没发,没有任何错误提示。
 *
 * 而 UI 上那张进度卡写着 "Running in background · you can switch tabs" —— 产品是承诺了可以走开的。
 *
 * 关键在于**帧其实到了,只是没人接**:`maybeIdleClose()` 遇到任何 `pendingReply` 就 return,
 * 所以 turn 未完成时 socket 不随页面卸载而关。实测佐证:页面卸载期间 `wschat:inflight` 被清掉了,
 * 而清它的 `clearInflight` 只在 manager 的 `route()` 收到 `message-complete` 时跑。
 *
 * 所以修法不碰传输,只是把「谁来接」从页面提到布局层(`PublishSignalExecutor`)。两条路径共用
 * 下面这个模块级的认领集合,所以同一个信号**恰好执行一次**:页面在场就页面做(它拿得到完整的
 * 稿件视图,更快),不在场就执行者兜底。
 *
 * ⚠️ 这里只覆盖「切页面/切会话」。刷新和掉线是另一回事 —— 那时帧**从未到达**客户端,
 * 谁也接不到,只能从历史补读,那会牵出「自动补发 vs 先问一句」的取舍,不在这个模块的范围内。
 */
import { publishToChannels, SHOPIFY_CHANNEL } from '../integration/directPublish';
import { listContentDrafts, approveContentDraft } from './contentDraftApi';
import { recordDraftPublished } from './calendarScheduleApi';
import { unwrapToolPayload } from '../../../components/agent-chat/ws-chat/toolPayload';
import type { WsChatToolEvent, WsChatTurnTrace } from './ws-chat/runtime';

/** 前端执行完后回传给 Maya 的那句话的前缀 —— 她靠它收尾,历史里也靠它判断「执行过了」。 */
export const PUBLISH_OUTCOME_PREFIX = 'Publish executed by the app —';

export interface PublishSignal {
  /** 工具调用 id —— 认领用的键。同一个信号只该被执行一次。 */
  callId: string;
  /** Maya 指名要发的那一篇(CREATE_CONTENT_DRAFT 的 draft_id)。 */
  draftId?: string;
  /** 她转达的商家已批准的渠道,空 = 这篇稿子本来的渠道。 */
  channels: string[];
  title?: string;
}

/**
 * 已被认领的信号。**模块级**,不是组件级 —— 页面卸载后它必须还在,否则页面路径和执行者
 * 会各发一次。同一个 app 生命周期内有效;整页刷新后清空,但刷新本就属于上面说的范围外情况。
 */
const claimed = new Set<string>();

/** 认领一个信号。返回 true 表示「这次由你执行」,false 表示别人已经接了。 */
export function claimPublishSignal(callId: string): boolean {
  if (claimed.has(callId)) return false;
  claimed.add(callId);
  return true;
}

/** 仅供测试:清空认领记录。 */
export function resetPublishSignalClaims(): void {
  claimed.clear();
}

/** 一个工具事件是不是「待执行的发布信号」。`duplicate` 是服务端 10s 闸的回答,不重复执行。 */
export function readPublishSignal(tool: WsChatToolEvent): PublishSignal | null {
  if (tool.kind !== 'call' || !/REQUEST_PUBLISH_EXECUTION/i.test(tool.name ?? '')) return null;
  if (tool.output == null) return null; // 还没有结果帧 —— 负载和去重标志都在里面
  const payload = unwrapToolPayload(tool.output, ['show_publish_executor', 'duplicate']);
  if (!payload || payload.duplicate === true || payload.show_publish_executor !== true) return null;
  return {
    callId: tool.id ?? `${tool.name}`,
    draftId: typeof payload.draft_id === 'string' ? payload.draft_id : undefined,
    channels: Array.isArray(payload.channels)
      ? (payload.channels as unknown[]).map(c => String(c).toLowerCase()).filter(Boolean)
      : [],
    title: typeof payload.title === 'string' ? payload.title : undefined,
  };
}

/**
 * 一段会话的**归档** trace 里最后一个待执行信号。
 *
 * 找 traces 而不是 `state.tools`:turn 一结束 `message-complete` 就把 tools 折进 trace 并清空
 * (`runtime.ts` 的 `case 'message-complete'`),而执行者正是在 turn 结束时才被叫醒的。
 */
export function findPublishSignalInTraces(
  traces: Record<string, WsChatTurnTrace>
): PublishSignal | null {
  const ids = Object.keys(traces);
  for (let i = ids.length - 1; i >= 0; i -= 1) {
    const tools = traces[ids[i]]?.tools ?? [];
    for (let t = tools.length - 1; t >= 0; t -= 1) {
      const signal = readPublishSignal(tools[t]);
      if (signal) return signal;
    }
  }
  return null;
}

/** 渠道 id(她说的)→ 直发栈认识的标签。认不出的渠道没有一键发布通路,直接丢掉。 */
export function channelLabel(raw: string): string | null {
  const c = raw.toLowerCase();
  if (c.includes('shopify') || c.includes('blog')) return SHOPIFY_CHANNEL;
  if (c === 'instagram') return 'Instagram';
  if (c === 'facebook') return 'Facebook';
  return null;
}

interface PersistedRow {
  id: string;
  clientDraftId?: string | null;
  title?: string | null;
  body?: string | null;
  platform?: string | null;
  contentType?: string | null;
  media?: string | null;
  imageUrl?: string | null;
  status?: string | null;
  updatedAt?: string | null;
}

/**
 * 挑出信号指名的那一行。
 *
 * **按 `draft_id` 认,而不是「最新那篇」** —— 页面上那条路径一直用的是 latestDraftView,
 * 同屏有多篇草稿时会发错稿(信号本来就带着 draft_id,只是没人用)。认不到再回落标题,
 * 最后才回落最近一条未丢弃的草稿。
 */
export function pickDraftRow(rows: PersistedRow[], signal: PublishSignal): PersistedRow | null {
  const live = rows.filter(r => r.status !== 'DISCARDED');
  if (signal.draftId) {
    const byId = live.find(r => r.clientDraftId === signal.draftId || r.id === signal.draftId);
    if (byId) return byId;
  }
  if (signal.title) {
    const wanted = signal.title.trim().toLowerCase();
    const byTitle = live.find(r => (r.title ?? '').trim().toLowerCase() === wanted);
    if (byTitle) return byTitle;
  }
  return live[0] ?? null;
}

function mediaItems(raw: string | null | undefined): { image_key?: string; image_url?: string }[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { items?: { image_key?: string; image_url?: string }[] };
    return Array.isArray(parsed?.items) ? parsed.items : [];
  } catch {
    return [];
  }
}

/** 这一行本来该发去哪 —— 信号没指定渠道时的回落。 */
export function rowChannels(row: PersistedRow): string[] {
  const platform = (row.platform ?? '').toLowerCase();
  if ((row.contentType ?? '').toLowerCase() === 'blog' || platform.includes('shopify')) {
    return [SHOPIFY_CHANNEL];
  }
  const label = channelLabel(platform);
  return label ? [label] : [];
}

export interface ExecuteResult {
  /** 回传给 Maya 的那句话 —— 没有可发的东西时也要说,不能静默。 */
  message: string;
  published: number;
}

/**
 * 执行一个信号,内容取自**已落库的草稿行**。
 *
 * 为什么不从聊天记录里解析稿件:落库的那一行才是服务端结算过的正文(博客的产品链接和 UTM
 * 是在 CREATE_CONTENT_DRAFT 里被服务端改写的,工具 args 里是模型的原稿),而且 Hub 里的编辑
 * 也写在这一行上。顺带的好处是这条路完全不碰页面态,所以布局层可以直接跑。
 *
 * 代价:这一行没有 tags 列,所以走这条路发的博客不带 tag —— 页面在场时仍走它自己那条完整路径。
 */
export async function executePublishSignal(params: {
  userId: string;
  signal: PublishSignal;
}): Promise<ExecuteResult> {
  const { userId, signal } = params;
  let rows: PersistedRow[];
  try {
    rows = (await listContentDrafts(userId)) as PersistedRow[];
  } catch {
    return {
      message: `${PUBLISH_OUTCOME_PREFIX} nothing was published: the app could not load your drafts just now. Ask me again and I'll retry.`,
      published: 0,
    };
  }
  const row = pickDraftRow(rows, signal);
  if (!row || !(row.body ?? '').trim()) {
    return {
      message: `${PUBLISH_OUTCOME_PREFIX} nothing was published: the app could not find that draft. Tell the merchant it is still unpublished.`,
      published: 0,
    };
  }

  const wanted = signal.channels.map(channelLabel).filter((x): x is string => !!x);
  const channels = Array.from(new Set(wanted.length > 0 ? wanted : rowChannels(row)));
  if (channels.length === 0) {
    return {
      message: `${PUBLISH_OUTCOME_PREFIX} nothing was published: no channel with a one-click path was named.`,
      published: 0,
    };
  }

  // 与按钮发布同一条语义:先把稿子标成 APPROVED,发布记录才挂得到这一行上。
  //
  // 这一步同时是闸门:approve 拒绝已丢弃的行(chatbot-api 的 approveUnlessPublished 把
  // DISCARDED 和 PUBLISHED 一起排除),把行原样回给我们。`pickDraftRow` 已经先过滤过
  // DISCARDED,但它只看得见**这次拉到的那份列表** —— 商家在 Hub 里丢弃与这条信号执行
  // 之间的那段时间不在它的视野里。状态回读是那扇后关的门:发不出去,也不谎报发出去了。
  const approved = await approveContentDraft(userId, row.id).catch(() => undefined);
  if (approved?.status === 'DISCARDED') {
    return {
      message: `${PUBLISH_OUTCOME_PREFIX} nothing was published: that draft was discarded in Marketing Hub. Tell the merchant it is gone and offer to write a fresh one.`,
      published: 0,
    };
  }

  const media = mediaItems(row.media);
  const outcomes = await publishToChannels({
    tenantId: userId,
    channels,
    draft: {
      title: row.title ?? 'Untitled',
      content: row.body ?? '',
      // tags 这一行上没有 —— 见上面的取舍说明。
      tags: [],
      image_url: media[0]?.image_url ?? row.imageUrl ?? undefined,
      media,
    },
    draftRowId: row.id,
    draftKeys: [row.clientDraftId, row.id],
  });

  const succeeded = outcomes.filter(o => o.ok);
  // ALREADY_PUBLISHED (§5.4): live already, nothing new went out — not booked a second time.
  const newlyPublished = succeeded.filter(o => !o.alreadyPublished);
  if (newlyPublished.length > 0) {
    // 日历回填 —— 尽力而为,和按钮发布同一条规矩:记账失败不影响发布结果的呈现。
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    void recordDraftPublished(
      userId,
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      `${pad(now.getHours())}:${pad(now.getMinutes())}`,
      newlyPublished.map(o => ({
        channel: o.label === SHOPIFY_CHANNEL ? 'shopify_blog' : o.label.toLowerCase(),
        draft_id: row.clientDraftId ?? null,
        title: row.title ?? null,
        external_id: o.externalId ?? null,
        external_url: o.url ?? null,
      }))
    ).catch(() => {});
  }

  const lines = outcomes.map(o =>
    o.ok
      ? `${o.label}: ${o.alreadyPublished ? 'already published' : 'published'}${o.url ? ` (${o.url})` : ''}`
      : `${o.label}: failed${o.message ? ` — ${o.message}` : ''}`
  );
  return { message: `${PUBLISH_OUTCOME_PREFIX} ${lines.join('; ')}`, published: succeeded.length };
}
